import { useCallback, useEffect, useRef, useState } from 'react';
import { isEmpty, isNil } from 'lodash';
import classNames from 'classnames';
// Import Types
import type { DropdownOption } from 'types';
import type { PopoverWithOptionListProps, GroupedOption } from './types';
// Import Hooks
import useOnClickOutside from 'hooks/UseOnClickOutside';
// Import Components
import PartnersOptionList from '../PartnersOptionList';
// Import UI Components
import { Button, Popover, SkeletonLoader } from '@geneui/components';
// Import Styles
import styles from './popoverWithOptionList.module.scss';

const PopoverWithOptionList: React.FC<PopoverWithOptionListProps> = (props) => {
    const {
        data,
        onOptionSelect,
        errorText,
        isValid = true,
        listHeight = 300,
        listWidth = 300,
        rowHeight = 40,
        buttonSelectText,
        columnTitles,
        isDisabled,
        isLoading,
        initialValue,
        extendTargetWidth = true,
    } = props;
    const isInvalid = isValid === false && !isEmpty(errorText);
    const [selectedOption, setSelectedOption] = useState<Nullable<DropdownOption<number>>>();

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const popoverRef = useRef(null);
    const popoverContentRef = useRef(null);

    const handleSelect = useCallback(
        (item: DropdownOption<number>) => {
            setSelectedOption(item);
            onOptionSelect(item);
            setIsPopoverOpen(false);
        },
        [onOptionSelect],
    );

    useEffect(() => {
        if (!isEmpty(data)) {
            if (!isNil(initialValue)) {
                const found = findItemByValue(initialValue, data);
                setSelectedOption(found);
            } else {
                setSelectedOption(null);
            }
        }
    }, [initialValue, data]);

    const findItemByValue = (initialValue: number | string, data: GroupedOption[]) => {
        const allItems = data.flatMap((group) => group.items);

        return allItems.find((item) => item.value === initialValue);
    };

    const toggleHandler = () => {
        setIsPopoverOpen((prev) => !prev);
    };

    useOnClickOutside(
        [popoverRef, popoverContentRef],
        useCallback(() => {
            if (isPopoverOpen) {
                setIsPopoverOpen(false);
            }
        }, [isPopoverOpen, setIsPopoverOpen]),
    );
    return (
        <div ref={popoverRef} className={styles.fullWidth}>
            <Popover
                isOpen={isPopoverOpen}
                extendTargetWidth={extendTargetWidth}
                minHeight={332}
                maxHeight={352}
                scrollbarNeeded={false}
                disableReposition={false}
                align="end"
                contentRef={popoverContentRef}
                Content={
                    <PartnersOptionList
                        groupsListData={data}
                        titles={columnTitles}
                        selectHandler={handleSelect}
                        listWidth={listWidth}
                        listHeight={listHeight}
                        listRowHeight={rowHeight}
                        selectedPartner={initialValue}
                    />
                }
            >
                <div className={styles.crmVirtualizedDropdownContainer}>
                    <SkeletonLoader isBusy={isLoading}>
                        <div className={styles.crmVirtualizedButtonWrapper}>
                            <Button
                                className={classNames(styles.crmVirtualizedButton, { [styles.inValid]: isInvalid })}
                                onClick={toggleHandler}
                                appearance="outline"
                                icon={isPopoverOpen ? 'bc-icon-arrow-up' : 'bc-icon-arrow-down'}
                                disabled={isDisabled}
                            >
                                <span className={selectedOption ? styles.selectedText : styles.placeholderText}>
                                    {selectedOption ? selectedOption.label : buttonSelectText}
                                </span>
                            </Button>

                            {isInvalid && <div className={styles.errorText}>{errorText}</div>}
                        </div>
                    </SkeletonLoader>
                </div>
            </Popover>
        </div>
    );
};
export default PopoverWithOptionList;
